import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';

import { Typography, Tabs, Tab } from '@mui/material';
import { withStyles } from '@mui/styles';

import { DateUtil, StoreUtil } from 'doctivity-shared/utils';
import { withRouter } from 'utils';
import {
  ProviderCommentsPage,
  ProviderCommentsReportPage,
  ProviderCommentDetailPage,
} from 'containers/pages';
import {
  getProviderComment,
  upsertProviderComment,
} from 'store/actions/providerCommentsActions';
import { showConfirmationDialog } from 'store/actions/systemActions';
import { ContainedSmallIconButton, ProviderCommentsDialog } from 'components';
import { MileageTrackingPage, MileageLogDetailPage } from 'mileage';

const styles = (theme) => ({
  header: {
    padding: theme.spacing(3),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    backgroundColor: theme.palette.primary.accent,
  },
  headerContainer: {
    marginBottom: theme.spacing(3),
  },
  headerTitle: {
    color: '#FFFFFF',
    fontSize: 24,
    paddingRight: 30,
  },
  tabs: {
    paddingTop: 3,
    paddingLeft: theme.spacing(3),
    backgroundColor: theme.palette.primary.accent,
  },
  indicator: {
    backgroundColor: '#FFFFFF',
    height: 2,
    marginBottom: 8,
  },
  tabPanel: {
    color: '#E0E0E0',
    '&.Mui-selected': {
      color: '#FFFFFF',
    },
    '&:hover': {
      color: '#FFFFFF',
    },
  },
});

class ProviderCommentsPageContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editDialogOpen: false,
      pendingSaveNavigation: false,
    };
  }

  componentDidMount() {
    const { dispatch, router, tab } = this.props;
    const id = router.params.id;
    const currentTab = tab || 'comments';

    if (id && currentTab === 'details') {
      dispatch(getProviderComment(id));
    }
  }

  componentDidUpdate(prevProps) {
    const { app, dispatch, router, editComment, tab } = this.props;
    const id = router.params.id;
    const currentTab = tab || 'comments';

    if (id && currentTab === 'details' && (app.selectedClient !== prevProps.app.selectedClient || id !== prevProps.router.params.id)) {
      dispatch(getProviderComment(id));
    }

    // Check if a save just completed and we should navigate to the new note
    if (this.state.pendingSaveNavigation && editComment !== prevProps.editComment) {
      const editData = StoreUtil.getData(editComment);
      if (editData && editData.id && !StoreUtil.isSaving(editComment)) {
        this.setState({ pendingSaveNavigation: false, editDialogOpen: false });
        router.navigate(`/notes/${editData.id}`);
      }
    }
  }

  render() {
    const { classes, app, comment, mileageLog } = this.props;

    const currentTab = this.props.tab || 'comments';

    return (
      <>
        <title>
          {comment
            ? `Activity Note - ${DateUtil.formatDateTimeFromDB(
                comment.comment_date
              )}`
            : 'Activity'}
        </title>
        <div className={classes.headerContainer}>
          <div className={classes.header}>
            <Typography className={classes.headerTitle}>
              Activity
            </Typography>
            <div style={{ display: 'flex', gap: 8 }}>
              <ContainedSmallIconButton
                icon='add'
                text='New Note'
                onClick={this.openEditDialog}
              />
            </div>
          </div>
          <Tabs
            variant='scrollable'
            className={classes.tabs}
            TabIndicatorProps={{ className: classes.indicator }}
            value={currentTab}
            onChange={this.onTabChange}
          >
            <Tab
              className={classes.tabPanel}
              label='Activity Notes'
              value='comments'
              disableRipple
            />
            <Tab
              className={classes.tabPanel}
              label='Mileage Tracking'
              value='tracking'
              disableRipple
            />
            <Tab
              className={classes.tabPanel}
              label='Reports'
              value='report'
              disableRipple
            />
            {(currentTab === 'details' || comment) && (
              <Tab
                className={classes.tabPanel}
                label={DateUtil.formatDateTimeFromDB(comment?.comment_date)}
                value='details'
                disableRipple
              />
            )}
            {currentTab === 'mileage_detail' && (
              <Tab
                className={classes.tabPanel}
                label={mileageLog?.date_of_trip ? DateUtil.formatDateForUser(mileageLog.date_of_trip) : 'Mileage Record'}
                value='mileage_detail'
                disableRipple
              />
            )}
          </Tabs>
        </div>
        {currentTab === 'comments' && <ProviderCommentsPage />}
        {currentTab === 'tracking' && <MileageTrackingPage />}
        {currentTab === 'report' && <ProviderCommentsReportPage />}
        {currentTab === 'details' && <ProviderCommentDetailPage />}
        {currentTab === 'mileage_detail' && <MileageLogDetailPage />}
        {this.state.editDialogOpen && (
          <ProviderCommentsDialog
            clientId={app.selectedClient}
            open={this.state.editDialogOpen}
            onSave={this.onSave}
            onClose={this.closeEditDialog}
          />
        )}
      </>
    );
  }

  onTabChange = (event, newValue) => {
    if (newValue === 'comments') {
      this.props.router.navigate('/notes');
    } else if (newValue === 'tracking') {
      this.props.router.navigate('/notes/mileage');
    } else if (newValue === 'report') {
      this.props.router.navigate('/notes_report');
    } else if (newValue === 'admin') {
      this.props.router.navigate('/notes_admin');
    }
    // else we are changing to the details tab with no id?
  };

  openEditDialog = () => {
    if (this.props.user.is_admin) {
      this.props.dispatch(
        showConfirmationDialog({
          title: 'Admin Users Can Not Create Notes',
          content: 'Login as a non-admin user to create activity notes.',
          confirmLabel: 'OK',
          onConfirm: () => {
            // do nothing
          },
        })
      );
    } else {
      this.setState({ editDialogOpen: true });
    }
  };

  closeEditDialog = () => {
    this.setState({ editDialogOpen: false });
  };

  onSave = (data) => {
    const { app, user, dispatch, tab } = this.props;
    const currentTab = tab || 'comments';

    if (
      data.body &&
      data.body.length > 0
      // &&
      //(data.providers.length > 0 ||
        //data.contacts.length > 0 ||
        //data.location_id)
    ) {
      data.client_id = app.selectedClient;
      data.user_id = user.id;

      // Navigate to new note on All or Details tabs (not on Reporting tab)
      const shouldNavigate = currentTab === 'comments' || currentTab === 'details';

      if (shouldNavigate) {
        // Keep the dialog open while the API call is in-flight so the list
        // page is never briefly exposed. Navigation will unmount the dialog.
        this.setState({ pendingSaveNavigation: true });
        dispatch(upsertProviderComment(data));
      } else {
        dispatch(upsertProviderComment(data));
        this.closeEditDialog();
      }
    } else {
      this.closeEditDialog();
    }
  };
}

ProviderCommentsPageContainer.propTypes = {
  app: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  classes: PropTypes.object,
  comment: PropTypes.object,
  editComment: PropTypes.object,
  tab: PropTypes.string,
  mileageLog: PropTypes.object,
};

function mapStateToProps(state, props) {
  const { app, providerComments, user } = state;
  const { router, tab } = props;
  const commentId = router.params.id;
  const isNoteTab = !tab || tab === 'comments' || tab === 'details';

  return {
    app,
    user,
    comment:
      isNoteTab && commentId &&
      StoreUtil.get(providerComments, StoreUtil.COMMON_ITEM, commentId).data,
    editComment: StoreUtil.get(providerComments, StoreUtil.COMMON_EDIT_ITEM),
    mileageLog: state.mileage?.log?.data || null,
  };
}

const styled = withStyles(styles)(ProviderCommentsPageContainer);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ProviderCommentsPageContainer };